import { CircleDollarSign, Trash2 } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { postJSON } from '@/lib/api'
import type { Invoice } from '@/pages/InvoicesPage.types'

interface ChargeNowResponse {
  invoice: Invoice
  charge_now: {
    outcome: 'succeeded' | 'failed' | 'skipped'
    failure_kind: 'soft' | 'hard' | 'needs_member_action' | null
    failure_reason: string | null
  }
}

export function useInvoiceChargeNow(invoiceId: number) {
  const queryClient = useQueryClient()
  const charge = useMutation({
    mutationFn: () => postJSON<ChargeNowResponse>(`/api/v1/invoices/${invoiceId}/charge_now`, {}),
    onSuccess: async (data) => {
      queryClient.setQueryData(['invoice', invoiceId], { invoice: data.invoice })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['invoices'] }),
        queryClient.invalidateQueries({ queryKey: ['autopay-recoveries'] }),
      ])
    },
  })

  return {
    charge: () => charge.mutate(),
    pending: charge.isPending,
    error: charge.isError,
    message: charge.data ? chargeNowOutcome(charge.data) : null,
  }
}

export function InvoiceHeaderActions({
  invoice,
  chargingNow,
  onChargeNow,
  onRecordPayment,
  onVoid,
}: {
  invoice: Invoice
  chargingNow: boolean
  onChargeNow: () => void
  onRecordPayment: () => void
  onVoid: () => void
}) {
  return (
    <div className="contents sm:flex sm:flex-wrap sm:items-start sm:justify-end sm:gap-2">
      {invoice.autopay_charge_now_available && (
        <Button type="button" size="sm" disabled={chargingNow} onClick={onChargeNow}>
          <CircleDollarSign className="mr-2 h-4 w-4" strokeWidth={1.5} />
          {chargingNow ? 'Charging...' : 'Charge now'}
        </Button>
      )}
      {invoice.balance_cents > 0 && invoice.status !== 'Voided' && (
        <Button
          type="button"
          size="sm"
          variant={invoice.autopay_charge_now_available ? 'outline' : 'default'}
          onClick={onRecordPayment}
        >
          <CircleDollarSign className="mr-2 h-4 w-4" strokeWidth={1.5} />
          Record payment
        </Button>
      )}
      {invoice.status !== 'Voided' && (
        <Button type="button" size="sm" variant="quietDestructive" onClick={onVoid}>
          <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
          Void invoice
        </Button>
      )}
      <InvoiceMetrics invoice={invoice} />
    </div>
  )
}

function chargeNowOutcome(response: ChargeNowResponse) {
  if (response.charge_now.outcome === 'succeeded') return 'Saved Card charge succeeded.'
  if (response.charge_now.outcome === 'skipped') return 'The Invoice no longer needs a charge.'

  return response.charge_now.failure_reason ?? 'The Saved Card charge failed.'
}

export function InvoiceMetrics({ invoice }: { invoice: Invoice }) {
  return (
    <dl
      data-testid="invoice-metrics"
      className="grid w-full grid-cols-3 divide-x divide-border rounded-md border border-border text-body-sm sm:flex sm:w-auto"
    >
      <DetailMetric label="Total" value={invoice.total} />
      <DetailMetric label="Balance" value={invoice.balance} />
      <DetailMetric label="Currency" value={invoice.currency} />
    </dl>
  )
}

function DetailMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 px-3 py-2 sm:flex-row sm:items-baseline sm:gap-2">
      <dt className="text-caption text-muted-foreground">{label}</dt>
      <dd className="font-medium text-foreground">{value}</dd>
    </div>
  )
}
